import ExcelJS from 'exceljs';
import { GfdPlaybook, PacketField, SeedInitiative } from './types';
import { valueToString, visiblePacketFields } from './mapper';

const PURPLE = 'FF221551';
const WHITE = 'FFFFFFFF';
const ORANGE = 'FFFFF1E6';
const ORANGE_TEXT = 'FF9A3412';
const ZEBRA = 'FFF8F7FC';
const HEADER_H = 28;
const EXCEL_MAX_CELL = 32767;
const EXCEL_MAX_ROW_PT = 409;

function slug(name: string): string {
  return (name || 'gfd-packet').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

/** Excel print headers treat & as a control character. */
function excelHeaderText(text: string): string {
  return String(text ?? '').replace(/&/g, '&&');
}

function excelCellText(text: string): string {
  const raw = String(text ?? '');
  if (raw.length <= EXCEL_MAX_CELL) return raw;
  return `${raw.slice(0, EXCEL_MAX_CELL - 14)}\n[truncated]`;
}

function wrappedLines(text: string, colWidth: number): number {
  const raw = String(text ?? '');
  if (!raw) return 1;
  const chars = Math.max(8, colWidth - 1);
  return raw.split(/\r?\n/).reduce((sum, line) => {
    const len = line.length || 1;
    return sum + Math.max(1, Math.ceil(len / chars));
  }, 0);
}

function rowHeight(lines: number): number {
  return Math.min(EXCEL_MAX_ROW_PT, Math.max(20, 14 * lines + 8));
}

export async function buildChampionWorkbook(
  playbook: GfdPlaybook,
  initiative: SeedInitiative,
  packet: PacketField[]
): Promise<ExcelJS.Workbook> {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Jarvis GFD Intake';
  wb.created = new Date();

  const widths = {
    done: 10,
    step: 8,
    section: 26,
    field: 38,
    action: 12,
    value: 52,
    clicks: 52,
    confirm: 22,
      why: 36,
      provenance: 16,
      source: 28,
  };

  const sheet = wb.addWorksheet('Champion checklist', {
    views: [{ state: 'frozen', ySplit: 1, showGridLines: true }],
    pageSetup: {
      orientation: 'landscape',
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      paperSize: 9,
      margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
    },
    headerFooter: {
      oddHeader: `&LGFD champion checklist&C${excelHeaderText(initiative.name)}&RPrepared for ${excelHeaderText(
        initiative.champion.name
      )}`,
    },
    properties: { defaultRowHeight: 20, tabColor: { argb: PURPLE } },
  });

  sheet.columns = [
    { header: 'Done?', key: 'done', width: widths.done },
    { header: 'Step', key: 'step', width: widths.step },
    { header: 'Section', key: 'section', width: widths.section },
    { header: 'Field', key: 'field', width: widths.field },
    { header: 'Action', key: 'action', width: widths.action },
    { header: 'Value / selection', key: 'value', width: widths.value },
    { header: 'Clicks', key: 'clicks', width: widths.clicks },
    { header: 'Confirm in ServiceNow', key: 'confirm', width: widths.confirm },
    { header: 'Provenance', key: 'provenance', width: widths.provenance },
    { header: 'Source', key: 'source', width: widths.source },
    { header: 'Why', key: 'why', width: widths.why },
  ];

  const header = sheet.getRow(1);
  header.font = { bold: true, color: { argb: WHITE }, name: 'Calibri', size: 11 };
  header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: PURPLE } };
  header.alignment = { vertical: 'middle', wrapText: true, horizontal: 'center' };
  header.height = HEADER_H;

  const visible = visiblePacketFields(packet);
  let step = 1;

  const addRow = (row: {
    section: string;
    field: string;
    action: string;
    value: string;
    clicks: string;
    confirm: string;
    provenance: string;
    source: string;
    why: string;
  }) => {
    const excelRow = sheet.addRow({
      done: 'No',
      step,
      section: excelCellText(row.section),
      field: excelCellText(row.field),
      action: excelCellText(row.action),
      value: excelCellText(row.value),
      clicks: excelCellText(row.clicks),
      confirm: excelCellText(row.confirm),
      provenance: excelCellText(row.provenance),
      source: excelCellText(row.source),
      why: excelCellText(row.why),
    });
    const confirmYes = /^yes/i.test(row.confirm);
    const zebra = step % 2 === 0;
    excelRow.alignment = { vertical: 'top', wrapText: true };
    excelRow.font = { name: 'Calibri', size: 10, color: { argb: 'FF222230' } };
    excelRow.height = rowHeight(
      Math.max(
        wrappedLines(row.field, widths.field),
        wrappedLines(row.value, widths.value),
        wrappedLines(row.clicks, widths.clicks),
        wrappedLines(row.why, widths.why),
        wrappedLines(row.section, widths.section)
      )
    );

    const fillArgb = confirmYes ? ORANGE : zebra ? ZEBRA : WHITE;
    for (let c = 1; c <= 11; c++) {
      const cell = excelRow.getCell(c);
      cell.alignment = { vertical: 'top', wrapText: true, horizontal: c === 1 || c === 2 || c === 5 ? 'center' : 'left' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillArgb } };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      };
    }

    const doneCell = excelRow.getCell('done');
    doneCell.value = 'No';
    doneCell.alignment = { vertical: 'middle', horizontal: 'center' };
    doneCell.dataValidation = {
      type: 'list',
      allowBlank: false,
      formulae: ['"Yes,No"'],
      showErrorMessage: true,
      errorTitle: 'Done?',
      error: 'Set Yes when this step is finished in ServiceNow.',
      promptTitle: 'Done?',
      prompt: 'Yes = finished in Employee Center.',
      showInputMessage: true,
    };

    excelRow.getCell('step').alignment = { vertical: 'top', horizontal: 'center' };
    excelRow.getCell('action').alignment = { vertical: 'top', horizontal: 'center' };
    excelRow.getCell('action').font = { name: 'Calibri', size: 10, bold: true, color: { argb: PURPLE } };

    if (confirmYes) {
      const confirmCell = excelRow.getCell('confirm');
      confirmCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: ORANGE_TEXT } };
    }

    step += 1;
  };

  addRow({
    section: 'Get to the form',
    field: 'Open Global Initiatives Intake Request',
    action: 'Navigate',
    value: playbook.navigation.prodUrl,
    clicks: playbook.navigation.steps.map((s, i) => `${i + 1}. ${s}`).join('\n'),
    confirm: 'No',
    provenance: '',
    source: '',
    why: 'Employee Center catalog item. Do not use Save as Draft.',
  });

  playbook.sections.forEach((section) => {
    const rows = visible.filter((row) => section.fields.some((f) => f.id === row.field.id));
    rows.forEach((row) => {
      const paste = ['free_text_short', 'free_text_long', 'number', 'currency', 'reference_lookup'].includes(
        row.field.fieldType
      );
      addRow({
        section: section.title,
        field: row.field.label,
        action: paste ? 'Paste' : row.field.fieldType === 'action_button' ? 'Click' : 'Select',
        value: valueToString(row.value),
        clicks: row.field.clickPath.map((s, i) => `${i + 1}. ${s}`).join('\n'),
        confirm: row.confirmInServiceNow ? 'YES - match live list' : 'No',
        provenance: row.state || '',
        source: [row.sourceDoc?.name, row.sourceSnippet].filter(Boolean).join(' — '),
        why: row.rationale,
      });
    });
  });

  playbook.attachments.forEach((a) => {
    addRow({
      section: 'Attachments',
      field: a.name,
      action: 'Upload',
      value: a.note,
      clicks: 'After Submit, paperclip on My Request.',
      confirm: 'No',
      provenance: '',
      source: '',
      why: a.required ? 'Required' : 'Optional but recommended',
    });
  });

  const lastRow = sheet.rowCount;
  sheet.autoFilter = `A1:K${lastRow}`;
  sheet.pageSetup.printArea = `A1:K${lastRow}`;
  sheet.pageSetup.printTitlesRow = '1:1';
  sheet.views = [{ state: 'frozen', ySplit: 1, zoomScale: 110, showGridLines: true }];

  const intro = wb.addWorksheet('How to use', {
    properties: { tabColor: { argb: 'FF57C8F4' } },
    pageSetup: { orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 1 },
  });
  intro.getColumn(1).width = 28;
  intro.getColumn(2).width = 90;

  const introLines: [string, string][] = [
    ['What this is', `Champion checklist for ${initiative.name}. Prepared for ${initiative.champion.name}.`],
    [
      'How to use Done?',
      'Each row is one click in ServiceNow Employee Center. Set Done? to Yes as you finish it. Filter Done? = No to see what is left.',
    ],
    [
      'Paste vs Select',
      'Paste: copy the Value / selection cell and paste into the form. Select: click the live dropdown - do not type the value unless it is a search box.',
    ],
    [
      'Orange rows',
      'Confirm in ServiceNow = YES means the live list wording may differ. Pick the closest match and screenshot the open dropdown if it does not match.',
    ],
    [
      'Save as Draft',
      'Do not use Save as Draft. It was not functional at go-live. Complete the form in one sitting from this sheet or the Word walkthrough.',
    ],
  ];
  playbook.warnings.forEach((w) => {
    if (introLines.some((pair) => pair[0] === w.title)) return;
    introLines.push([w.title, w.text]);
  });

  intro.getRow(1).values = ['Champion checklist', 'Global Front Door'];
  intro.getRow(1).font = { bold: true, size: 14, color: { argb: PURPLE }, name: 'Calibri' };
  intro.getRow(1).height = 24;

  introLines.forEach((pair, i) => {
    const row = intro.getRow(i + 3);
    row.getCell(1).value = pair[0];
    row.getCell(1).font = { bold: true, name: 'Calibri', size: 11, color: { argb: PURPLE } };
    row.getCell(1).alignment = { vertical: 'top', wrapText: true };
    row.getCell(2).value = pair[1];
    row.getCell(2).font = { name: 'Calibri', size: 11 };
    row.getCell(2).alignment = { vertical: 'top', wrapText: true };
    row.height = rowHeight(Math.max(2, wrappedLines(pair[1], 90)));
  });

  return wb;
}

export async function downloadChampionExcel(
  playbook: GfdPlaybook,
  initiative: SeedInitiative,
  packet: PacketField[]
): Promise<void> {
  const wb = await buildChampionWorkbook(playbook, initiative, packet);
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([new Uint8Array(buf as ArrayBuffer)], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `gfd-champion-checklist-${slug(initiative.name)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
